import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, InputSelect, InputTypeahead } from '@farmshare/ui-components';
import { resolveHangTimeDays } from '@farmshare/utils';
import { FormikProvider, useFormikContext } from 'formik';
import { useRecoilValue } from 'recoil';
import { userState, vendorState } from 'state';

import {
  EnumUserType,
  ProcessorSettingsHangTimeSettings,
  ProcessorSettingsHangTimeSettingsOptions,
  User,
  useStateManyQuery,
} from '../../../../lib/graphql';
import { useSettings } from 'providers';

import {
  extractPhoneDigits,
  formatPhoneDisplay,
  isPhoneNumberValid,
} from '../../helpers/formatting';
import { getUserTypeOptions } from '../../helpers/customer-form-helpers';
import { useCustomerOptions } from '../../hooks/use-customer-options';
import { useCustomerForm } from '../../hooks/use-customer-form';
import type { CustomerFormValues } from '../../helpers/customer-validation';

function HangTimeFields() {
  const { settings } = useSettings();
  const { values, setFieldValue } = useFormikContext<CustomerFormValues>();
  const isProducer = String(values.type).toLowerCase() === 'producer';
  // Depend on the Apollo-cached array itself, not the context value: the
  // settings context object is rebuilt whenever the query result churns, but
  // this nested array keeps its identity while the cached data is unchanged.
  const configuredHangTimeSettings =
    settings?.data?.processorSettingsOne?.hangTimeSettings;

  const hangTimeSettings: ProcessorSettingsHangTimeSettings[] = useMemo(
    () =>
      isProducer
        ? (configuredHangTimeSettings ?? []).filter(
            (s): s is ProcessorSettingsHangTimeSettings =>
              Boolean(s?.isEnabled),
          )
        : [],
    [isProducer, configuredHangTimeSettings],
  );

  // Memoized so each species' `options` array keeps a stable identity across
  // renders. InputSelect's autoSelectFirstOption effect watches `options` by
  // reference, so rebuilding these inline made a single-option species loop
  // forever: render -> new array -> setFieldValue -> render (PRO-467 follow-up).
  const hangTimeFields = useMemo(
    () =>
      hangTimeSettings.map((speciesConfig) => ({
        species: speciesConfig.species,
        label: `${
          speciesConfig.species.charAt(0).toUpperCase() +
          speciesConfig.species.slice(1)
        } Hang Time`,
        options: (speciesConfig.options ?? [])
          .filter((opt): opt is ProcessorSettingsHangTimeSettingsOptions =>
            Boolean(opt),
          )
          .map((opt) => {
            const parts = [`${opt.durationDays} days`];
            if ((opt.upcharge ?? 0) > 0) parts.push(`(+$${opt.upcharge})`);
            if (opt.isDefault) parts.push('(default)');
            return {
              value: String(opt.durationDays),
              label: parts.join(' '),
            };
          }),
      })),
    [hangTimeSettings],
  );

  // Initialize default values for species that don't have a preference set
  useEffect(() => {
    hangTimeSettings.forEach((speciesConfig) => {
      // Type lies: declared `number | undefined`, runtime is `string` (see PRO-467).
      const currentVal = values.hangTimePreferences?.[speciesConfig.species] as
        | number
        | string
        | undefined;
      if (currentVal === undefined || currentVal === '') {
        const { days } = resolveHangTimeDays({
          species: speciesConfig.species,
          hangTimeSettings: [speciesConfig],
        });
        if (days != null) {
          setFieldValue(
            `hangTimePreferences.${speciesConfig.species}`,
            String(days),
          );
        }
      }
    });
    // `values` is deliberately not a dep: this seeds defaults when the species
    // set changes, and re-running it on every keystroke would fight the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hangTimeSettings]);

  if (hangTimeFields.length === 0) return null;

  return (
    <>
      {hangTimeFields.map((field) => (
        <InputSelect
          key={field.species}
          label={field.label}
          nameOverride={`hangTimePreferences.${field.species}`}
          options={field.options}
        />
      ))}
    </>
  );
}

// Helper component to handle nested producer dialog
// This is defined here to avoid circular dependency issues
function NestedProducerDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (created: User) => void | Promise<void>;
}) {
  // Lazy import to avoid circular dependency
  const AddCustomerDialog = lazy(
    () => import('../add-customer-dialog/add-customer-dialog'),
  );

  return (
    <Suspense fallback={null}>
      <AddCustomerDialog
        key="add-producer-dialog"
        open={open}
        onClose={onClose}
        titleOverride="Add Producer"
        forcedType={EnumUserType.Producer}
        onSuccess={onSuccess}
      />
    </Suspense>
  );
}

export interface CustomerFormProps {
  customer?: User | null;
  forcedType?: EnumUserType;
  onSuccess?: (created: User) => void | Promise<void> | undefined;
  onCancel: () => void;
  /** Called after successful save - defaults to onCancel if not provided */
  onClose?: () => void;
  /** Whether the form is being used inside a dialog (affects producer options loading) */
  isInDialog?: boolean;
  /** Show action buttons (Save/Cancel) */
  showActions?: boolean;
  /** Custom action buttons to render instead of default Save/Cancel */
  customActions?: React.ReactNode;
  /** ID prefix for form fields (useful when multiple forms on same page) */
  idPrefix?: string;
  /** Render as div instead of form element (use when nested in another form) */
  renderAsDiv?: boolean;
  /** Hide the address section entirely */
  hideAddressSection?: boolean;
  /** When true, do not upsert on save; instead return validated values */
  deferMode?: boolean;
  /** Callback when deferMode is true and form is saved with valid values */
  onDeferSubmit?: (vals: CustomerFormValues) => void | Promise<void>;
  /** Optional context to tweak form behavior (e.g., hide producer selection when embedded in scheduling contact) */
  context?: 'scheduling-contact' | string;
  /** If provided and context is scheduling-contact with forcedType Buyer, preset relationships.producerId */
  presetProducerId?: string;
  /** Optionally override vendorId instead of reading from vendorState */
  vendorId?: string;
  /** Prefix to add to all field names (e.g., 'splitAssignments.0') - makes fields part of parent form */
  prefix?: string;
  /** The host modal's `setBusy`, so it can't be closed while a save is in flight */
  setBusy?: (busy: boolean) => void;
}

export function CustomerForm({
  customer = null,
  forcedType = undefined,
  onSuccess = undefined,
  onCancel,
  onClose = undefined,
  isInDialog = false,
  showActions = true,
  customActions = undefined,
  idPrefix = 'customer',
  renderAsDiv = false,
  hideAddressSection = false,
  deferMode = false,
  onDeferSubmit = undefined,
  context = undefined,
  presetProducerId = undefined,
  vendorId: vendorIdOverride = undefined,
  prefix = undefined,
  setBusy = undefined,
}: CustomerFormProps) {
  const currentUser = useRecoilValue(userState);
  const vendor = useRecoilValue(vendorState);
  const currentVendorId = vendor?._id;
  const effectiveVendorId = vendorIdOverride ?? currentVendorId;

  // Helper function to get field name with optional prefix
  const getName = (fieldName: string) =>
    prefix ? `${prefix}.${fieldName}` : fieldName;

  // When prefix is provided, use parent Formik context instead of creating our own.
  // The parent form's shape is genuinely unknown here — CustomerForm is embedded
  // at different sub-paths (`recurrence`, `requesterContactInformation`,
  // `splitBuyers.<index>.<slot>`) of unrelated parent forms.
  const parentFormik = prefix
    ? useFormikContext<Record<string, unknown>>()
    : null;

  const {
    formik: ownFormik,
    loading,
    cancelForm,
    shouldShowError: ownShouldShowError,
    fieldProps: ownFieldProps,
  } = useCustomerForm({
    customer,
    forcedType,
    vendorId: effectiveVendorId,
    onSuccess,
    onClose: onClose ?? onCancel,
    deferMode,
    onDeferSubmit,
    setBusy,
  });

  // Use parent formik if prefix is provided, otherwise use our own
  const formik = parentFormik || ownFormik;

  // Helper to get nested value from formik values
  const getNestedValue = (path: string): unknown => {
    const keys = path.split('.');
    let value: unknown = formik.values;
    for (const key of keys) {
      value = (value as Record<string, unknown> | undefined)?.[key];
      if (value === undefined) break;
    }
    return value;
  };

  // Helper to get nested touched/error state
  const getNestedProperty = (obj: unknown, path: string): unknown => {
    const keys = path.split('.');
    let value: unknown = obj;
    for (const key of keys) {
      value = (value as Record<string, unknown> | undefined)?.[key];
      if (value === undefined) break;
    }
    return value;
  };

  // Simplified field error checking when using parent context
  const shouldShowError: (name: string) => boolean = prefix
    ? (name: string) => {
        const fullName = getName(name);
        return Boolean(
          getNestedProperty(formik.touched, fullName) &&
            getNestedProperty(formik.errors, fullName),
        );
      }
    : (name: string) => ownShouldShowError(name as keyof CustomerFormValues);

  // Field props helper that works with prefix
  const fieldProps = prefix
    ? (name: string) => {
        const fullName = getName(name);
        const err = getNestedProperty(formik.errors, fullName);
        const errMessage = typeof err === 'string' ? err : undefined;
        return {
          name: fullName,
          value: (getNestedValue(fullName) as string | undefined) || '',
          onChange: formik.handleChange,
          onBlur: formik.handleBlur,
          error: shouldShowError(name),
          helperText: shouldShowError(name) ? errMessage : undefined,
        };
      }
    : ownFieldProps;

  const [addNewProducer, setAddNewProducer] = useState(false);
  const [_pendingProducerText, setPendingProducerText] = useState('');
  const [addressOpen, setAddressOpen] = useState(false);
  const [phoneWarning, setPhoneWarning] = useState<string>('');
  // no-op helper detection removed; we will stabilize layout via fixed height wrapper

  // Get user type options
  const userTypeOptions = getUserTypeOptions(currentUser?.role ?? undefined);

  // Get producers for producer select
  const canLoadProducers = Boolean(
    effectiveVendorId && isInDialog && currentUser && !deferMode,
  );

  const {
    customerOptions: producerOptions,
    refetchCustomers: refetchProducers,
  } = useCustomerOptions(effectiveVendorId, canLoadProducers, {
    relationshipType: EnumUserType.Producer,
  });

  // Get states for state select
  const { data: statesData, loading: statesLoading } = useStateManyQuery({
    skip: deferMode || !currentUser,
  });
  const states = statesData?.stateMany || [];
  const getStatesField = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      label="State"
      name="address_state"
      autoComplete="address-level1"
      error={Boolean(
        shouldShowError('address_state') &&
          getNestedProperty(formik.errors, 'address_state'),
      )}
      helperText={
        shouldShowError('address_state')
          ? (getNestedProperty(formik.errors, 'address_state') as
              | string
              | undefined)
          : undefined
      }
    />
  );

  // When a forced type is provided, keep the form value in sync
  useEffect(() => {
    if (forcedType && getNestedValue('type') !== forcedType) {
      formik.setFieldValue('type', forcedType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedType]);

  // Reset producerId when user type changes to non-buyer
  useEffect(() => {
    if (
      getNestedValue('type') !== EnumUserType.Buyer &&
      getNestedProperty(getNestedValue('relationships'), 'producerId')
    ) {
      formik.setFieldValue('relationships.producerId', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNestedValue('type')]);

  // When in scheduling-contact context, preset the producerId and hide selector
  useEffect(() => {
    if (
      context === 'scheduling-contact' &&
      getNestedValue('type') === EnumUserType.Buyer &&
      presetProducerId &&
      !getNestedProperty(getNestedValue('relationships'), 'producerId')
    ) {
      formik.setFieldValue('relationships.producerId', presetProducerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, presetProducerId, getNestedValue('type')]);

  // Validate phone number and show warning if invalid
  useEffect(() => {
    const phone = (
      prefix ? getNestedValue(getName('phone')) : getNestedValue('phone')
    ) as string | undefined;
    if (phone && phone.length >= 10) {
      if (!isPhoneNumberValid(phone)) {
        setPhoneWarning('This phone number may be invalid');
      } else {
        setPhoneWarning('');
      }
    } else {
      setPhoneWarning('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix ? getNestedValue(getName('phone')) : getNestedValue('phone')]);

  const formContent = (
    <Box
      component={renderAsDiv ? 'div' : 'form'}
      onSubmit={renderAsDiv ? undefined : formik.handleSubmit}
      noValidate={renderAsDiv ? undefined : true}
      role={renderAsDiv ? undefined : 'form'}
      aria-label="Customer form"
    >
      <Box display="grid" gap={2}>
        {!forcedType && (
          <InputSelect
            label="Customer Type"
            nameOverride="type"
            options={userTypeOptions}
            autoSelectFirstOption={false}
            required
            aria-label="Select Customer Type"
          />
        )}
        {getNestedValue('type') === EnumUserType.Buyer &&
          context !== 'scheduling-contact' && (
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                alignItems: { sm: 'center' },
              }}
            >
              <InputTypeahead
                label="Producer"
                nameOverride="relationships.producerId"
                options={producerOptions}
                labelKey="fullName"
                required
                addNoResultsOptionLabel="Add New Producer"
                actions={{
                  addNew: ({ inputValue }) => {
                    setPendingProducerText(inputValue ?? '');
                    setAddNewProducer(true);
                  },
                }}
                aria-label="Select Producer"
              />
              <Button
                variant="outlined"
                content="+ Add"
                onClick={() => {
                  setPendingProducerText('');
                  setAddNewProducer(true);
                }}
                size="lg"
                sx={{
                  alignSelf: { sm: 'start' },
                  minWidth: '120px',
                  width: { xs: '100%', sm: 'auto' },
                  mt: 0.85,
                  ml: { xs: 0, sm: 1 },
                }}
              />
            </Box>
          )}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
        >
          <TextField
            label="First name"
            id={`${idPrefix}-first-name`}
            {...fieldProps('first_name')}
            name={getName('first_name')}
            autoComplete="given-name"
            fullWidth
            aria-label="First Name"
            slotProps={{
              input: {
                inputProps: {
                  inputMode: 'text',
                  pattern: '[a-zA-Z]*',
                },
              },
            }}
          />
          <TextField
            label="Last name"
            id={`${idPrefix}-last-name`}
            {...fieldProps('last_name')}
            name={getName('last_name')}
            autoComplete="family-name"
            fullWidth
            aria-label="Last Name"
            slotProps={{
              input: {
                inputProps: {
                  inputMode: 'text',
                  pattern: '[a-zA-Z]*',
                },
              },
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0 }}>
          * Enter at least one of first name, last name, or company
        </Typography>
        <Typography
          id={`${idPrefix}-contact-note`}
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0 }}
        >
          * At least one contact method (email or phone) is required
        </Typography>
        <TextField
          label="Phone"
          name={getName('phone')}
          id={`${idPrefix}-phone`}
          value={formatPhoneDisplay(
            (prefix
              ? getNestedValue(getName('phone'))
              : getNestedValue('phone')) as string | null | undefined,
          )}
          onChange={(e) => {
            const digits = extractPhoneDigits(e.target.value);
            formik.setFieldValue(getName('phone'), digits);
          }}
          onBlur={formik.handleBlur}
          error={shouldShowError('phone')}
          helperText={
            (shouldShowError('phone') &&
              ((prefix
                ? getNestedProperty(formik.errors, getName('phone'))
                : getNestedProperty(formik.errors, 'phone')) as
                | string
                | undefined)) ||
            (phoneWarning && (
              <Typography
                component="span"
                variant="caption"
                sx={{ color: 'warning.main' }}
              >
                {phoneWarning}
              </Typography>
            )) ||
            undefined
          }
          placeholder="(555) 123-4567"
          autoComplete="tel"
          slotProps={{
            input: {
              inputMode: 'tel',
              'aria-describedby': `${idPrefix}-contact-note`,
            },
          }}
          fullWidth
          aria-label="Phone"
        />
        <TextField
          type="email"
          label="Email"
          id={`${idPrefix}-email`}
          {...fieldProps('email')}
          autoComplete="email"
          slotProps={{
            input: {
              inputMode: 'email',
              'aria-describedby': `${idPrefix}-contact-note`,
            },
          }}
          fullWidth
          aria-label="Email"
        />
        <TextField
          label="Company"
          id={`${idPrefix}-company`}
          {...fieldProps('company')}
          autoComplete="organization"
          fullWidth
          aria-label="Company"
          slotProps={{
            input: {
              inputProps: {
                inputMode: 'text',
                pattern: '[a-zA-Z]*',
              },
            },
          }}
        />
        {!hideAddressSection && (
          <Accordion
            disableGutters
            expanded={addressOpen}
            onChange={(_, isExpanded) => setAddressOpen(isExpanded)}
            sx={{
              boxShadow: 'none',
              m: 0,
              p: 0,
              border: '1px solid #ccc',
              bgcolor: 'transparent',
              backgroundImage: 'none',
              borderRadius: '4px',
              '&.Mui-expanded': { m: 0 },
            }}
          >
            <AccordionSummary
              id={`${idPrefix}-address-summary`}
              aria-controls={`${idPrefix}-address-content`}
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 1,
                bgcolor: 'transparent',
                '&.Mui-expanded': { m: 0, bgcolor: 'transparent' },
              }}
            >
              <Typography variant="body1">
                Address Information{' '}
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >
                  {addressOpen ? '(click to close)' : '(click to expand)'}
                </Typography>
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              id={`${idPrefix}-address-content`}
              aria-labelledby={`${idPrefix}-address-summary`}
              sx={{ p: 2, pt: 0 }}
            >
              <Box display="grid" gap={2}>
                <TextField
                  label="Street"
                  id={`${idPrefix}-address-street`}
                  {...fieldProps('address_street')}
                  autoComplete="address-line1"
                  fullWidth
                  aria-label="Address"
                  slotProps={{
                    input: {
                      inputProps: {
                        inputMode: 'text',
                        pattern: '[a-zA-Z]*',
                      },
                    },
                  }}
                />
                <TextField
                  label="Address 2 (optional)"
                  id={`${idPrefix}-address-street2`}
                  {...fieldProps('address_street2')}
                  autoComplete="address-line2"
                  fullWidth
                  aria-label="Address 2 (optional)"
                  slotProps={{
                    input: {
                      inputProps: {
                        inputMode: 'text',
                        pattern: '[a-zA-Z]*',
                      },
                    },
                  }}
                />
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '2fr 1fr 1fr',
                    },
                  }}
                >
                  <TextField
                    label="City"
                    id={`${idPrefix}-address-city`}
                    {...fieldProps('address_city')}
                    autoComplete="address-level2"
                    fullWidth
                    aria-label="City"
                    slotProps={{
                      input: {
                        inputProps: {
                          inputMode: 'text',
                          pattern: '[a-zA-Z]*',
                        },
                      },
                    }}
                  />
                  <Autocomplete
                    getOptionLabel={(option) => option.name}
                    loading={statesLoading}
                    options={states}
                    filterOptions={(options, { inputValue }) => {
                      const q = inputValue.trim().toLowerCase();
                      if (!q) return options;
                      return options.filter(
                        (o) =>
                          o.name.toLowerCase().includes(q) ||
                          o.code.toLowerCase().includes(q),
                      );
                    }}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    onBlur={formik.handleBlur}
                    onChange={(_, newValue) => {
                      formik.setFieldValue(
                        'address_state',
                        newValue?.code || '',
                      );
                    }}
                    renderInput={(params) => getStatesField(params)}
                    renderOption={(props, option) => (
                      <li {...props}>{option.name}</li>
                    )}
                    value={
                      states.find(
                        (s) => s.code === getNestedValue('address_state'),
                      ) || null
                    }
                  />
                  <TextField
                    label="ZIP"
                    id={`${idPrefix}-address-postcode`}
                    {...fieldProps('address_postcode')}
                    autoComplete="postal-code"
                    slotProps={{
                      input: {
                        inputProps: {
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                        },
                      },
                    }}
                    fullWidth
                    aria-label="ZIP"
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
        <HangTimeFields />
      </Box>
      {addNewProducer && (
        <NestedProducerDialog
          open={addNewProducer}
          onClose={() => setAddNewProducer(false)}
          onSuccess={async (created) => {
            await refetchProducers?.();
            formik.setFieldValue('relationships.producerId', created._id);
            setAddNewProducer(false);
          }}
        />
      )}
      {showActions && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 4,
            // In a dialog the form is taller than a phone screen, and Save
            // and Cancel sat at the very end of the scroll. Pin them to the
            // bottom of the dialog's scroll area, over the fields, the way a
            // dialog's own action bar would be.
            ...(isInDialog && {
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
              mt: 2,
              pt: 1.5,
              pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
              bgcolor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider',
            }),
          }}
        >
          {customActions || (
            <>
              <Button variant="text" onClick={cancelForm} content="Cancel" />
              <Button
                type={renderAsDiv ? 'button' : 'submit'}
                onClick={renderAsDiv ? () => formik.handleSubmit() : undefined}
                variant="contained"
                isLoading={formik.isSubmitting || loading}
                content="Save"
              />
            </>
          )}
        </Box>
      )}
    </Box>
  );

  // Only wrap in FormikProvider if we're not using the parent context
  return prefix ? (
    formContent
  ) : (
    <FormikProvider value={formik}>{formContent}</FormikProvider>
  );
}
